"use client";

import { ReactNode } from "react";
import Button, { ButtonType } from "@/app/(components)/generics/Button/Button";
import EditableText from "@/app/(components)/generics/EditableText/EditableText";
import IconExternalLink from "@/app/(components)/generics/Icons/IconExternalLink";
import SiteFavicon from "@/app/(components)/generics/SiteFavicon/SiteFavicon";
import { TKey } from "@/app/translations/TKey";
import { useT } from "@/app/translations/useT";
import { domainOf, normalizeUrl } from "./BrowserFrame.logic";
import styles from "./BrowserFrame.module.css";

type Props = {
  link?: string;
  canEdit: boolean;
  linkMaxLength: number;
  onSaveLink: (value: string) => void;
  children: ReactNode;
};

export default function BrowserFrame(props: Props) {
  const { link, canEdit, linkMaxLength, onSaveLink, children } = props;
  const T = useT();

  const open = () => {
    if (link) {
      window.open(normalizeUrl(link), "_blank", "noopener,noreferrer");
    }
  };

  return (
    <div className={styles.frame}>
      <div className={styles.bar}>
        <span className={styles.dots}>
          <i />
          <i />
          <i />
        </span>

        <div className={styles.address}>
          {link && <SiteFavicon link={link} className={styles.favicon} />}
          <EditableText
            value={link ?? ""}
            displayText={link ? domainOf(link) : undefined}
            maxLength={linkMaxLength}
            onSave={onSaveLink}
            placeholder={T(TKey.PROJECT_LINK_PLACEHOLDER) as string}
            fitContent
            fullWidthOnEdit
            alignCenter
            disabled={!canEdit}
            startEditingOnMount={!link && canEdit}
            className={styles.addressEdit}
            textClassName={styles.addressText}
            inputClassName={styles.addressInput}
          />
        </div>

        {link && (
          <Button
            min={true}
            isLink={false}
            type={ButtonType.SOFT_GREEN}
            text={T(TKey.PROJECT_SITE_VISIT) as string}
            beforeIcon={<IconExternalLink size={14} strokeWidth={2} />}
            onClick={open}
          />
        )}
      </div>

      <div className={styles.content}>{children}</div>
    </div>
  );
}
